import { useState } from "react";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  AdminViewToggle,
  useAdminViewMode,
} from "@jambushrushti/components/admin/AdminViewToggle";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Input } from "@jambushrushti/components/ui/input";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import { Label } from "@jambushrushti/components/ui/label";
import { Switch } from "@jambushrushti/components/ui/switch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@jambushrushti/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  Plus,
  Pencil,
  Trash2,
  ExternalLink,
  FileText,
  Loader2,
  X,
} from "lucide-react";
import {
  useAllEmagazines,
  Emagazine,
  EMAGAZINE_ENDPOINT,
  parseEmagazineResponse,
} from "@jambushrushti/hooks/useEmagazines";
import { cmsFetch } from "@jambushrushti/services/adminAuth";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format, isValid } from "date-fns";
import {
  IMAGE_ACCEPT,
  toMultipart,
  validateImageFile,
} from "@jambushrushti/lib/multipart";

export default function EmagazineManagement() {
  const [viewMode, setViewMode] = useAdminViewMode();
  const { data: emagazines, isLoading } = useAllEmagazines();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Emagazine | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    volume: "",
    issue_date: "",
    year: new Date().getFullYear(),
    pdf_url: "",
    description: "",
    is_published: true,
  });

  const handleRemoveImage = () => {
    setCoverImage(null);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      volume: "",
      issue_date: "",
      year: new Date().getFullYear(),
      pdf_url: "",
      description: "",
      is_published: true,
    });
    setEditingItem(null);
    setCoverImage(null);
  };

  const handleEdit = (item: Emagazine) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      volume: item.volume || "",
      issue_date: item.issue_date?.slice(0, 10) || "",
      year: item.year,
      pdf_url: item.pdf_url,
      description: item.description || "",
      is_published: item.is_published,
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        title: formData.title,
        volume: formData.volume || null,
        issue_date: formData.issue_date || null,
        year: formData.year,
        pdf_url: formData.pdf_url,
        description: formData.description || null,
        is_published: formData.is_published,
      };

      if (editingItem) {
        await parseEmagazineResponse(
          await cmsFetch(`${EMAGAZINE_ENDPOINT}${editingItem.id}/`, {
            method: "PATCH",
            body: toMultipart(payload, "cover_image", coverImage),
          })
        );
        toast.success("eMagazine updated successfully");
      } else {
        await parseEmagazineResponse(
          await cmsFetch(EMAGAZINE_ENDPOINT, {
            method: "POST",
            body: toMultipart(payload, "cover_image", coverImage),
          })
        );
        toast.success("eMagazine added successfully");
      }

      queryClient.invalidateQueries({ queryKey: ["emagazines"] });
      queryClient.invalidateQueries({ queryKey: ["emagazines-all"] });
      queryClient.invalidateQueries({ queryKey: ["emagazines-by-year"] });
      setIsDialogOpen(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || "Failed to save eMagazine");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this eMagazine?")) {
      return;
    }

    try {
      await parseEmagazineResponse(
        await cmsFetch(`${EMAGAZINE_ENDPOINT}${id}/`, {
          method: "DELETE",
        })
      );
      toast.success("eMagazine deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["emagazines"] });
      queryClient.invalidateQueries({ queryKey: ["emagazines-all"] });
      queryClient.invalidateQueries({ queryKey: ["emagazines-by-year"] });
    } catch (error: any) {
      toast.error(error.message || "Failed to delete eMagazine");
    }
  };

  return (
    <AdminLayout title="eMagazine Management">
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground sm:text-2xl">
              Jambu Jyoti eMagazine
            </h2>
            <p className="text-muted-foreground">
              Manage eMagazine issues and archives
            </p>
          </div>
          <Dialog
            open={isDialogOpen}
            onOpenChange={open => {
              setIsDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button className="w-fit gap-2 md:w-auto">
                <Plus className="h-4 w-4" />
                Add Issue
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingItem ? "Edit eMagazine" : "Add New eMagazine Issue"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={e =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      placeholder="e.g., Jambu Jyoti Vol. 6"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="volume">Volume</Label>
                    <Input
                      id="volume"
                      value={formData.volume}
                      onChange={e =>
                        setFormData({ ...formData, volume: e.target.value })
                      }
                      placeholder="e.g., Vol. 6"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="year">Year *</Label>
                    <Input
                      id="year"
                      type="number"
                      value={formData.year}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          year: parseInt(e.target.value),
                        })
                      }
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="issue_date">Issue Date</Label>
                    <Input
                      id="issue_date"
                      type="date"
                      value={formData.issue_date}
                      onChange={e =>
                        setFormData({ ...formData, issue_date: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pdf_url">PDF URL *</Label>
                  <Input
                    id="pdf_url"
                    value={formData.pdf_url}
                    onChange={e =>
                      setFormData({ ...formData, pdf_url: e.target.value })
                    }
                    placeholder="https://example.com/magazine.pdf"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Cover Image</Label>
                  <div className="flex flex-col gap-3">
                    <Input
                      id="cover_image"
                      type="file"
                      accept={IMAGE_ACCEPT}
                      onChange={e => {
                        const file = e.target.files?.[0] || null;
                        if (!file) {
                          return setCoverImage(null);
                        }
                        try {
                          validateImageFile(file);
                          setCoverImage(file);
                        } catch (error) {
                          e.target.value = "";
                          toast.error((error as Error).message);
                        }
                      }}
                    />

                    {/* Preview */}
                    {editingItem?.cover_image && !coverImage && (
                      <div className="relative inline-block">
                        <img
                          src={editingItem.cover_image}
                          alt="Cover preview"
                          className="h-auto w-32 rounded-md border"
                          onError={e => {
                            (e.target as HTMLImageElement).src =
                              "/placeholder.svg";
                          }}
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute -right-2 -top-2 h-6 w-6"
                          onClick={handleRemoveImage}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={e =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Brief description of this issue..."
                    rows={3}
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="is_published"
                    checked={formData.is_published}
                    onCheckedChange={checked =>
                      setFormData({ ...formData, is_published: checked })
                    }
                  />
                  <Label htmlFor="is_published">Published</Label>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {editingItem ? "Update" : "Add"} Issue
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3 gap-y-3">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" /> All Issues
              </CardTitle>
              <AdminViewToggle value={viewMode} onChange={setViewMode} />
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : emagazines && emagazines.length > 0 && viewMode === "table" ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Volume</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>PDF</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emagazines.map(item => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        {item.title}
                      </TableCell>
                      <TableCell>{item.volume || "-"}</TableCell>
                      <TableCell>{item.year}</TableCell>
                      <TableCell>
                        {item.issue_date && isValid(new Date(item.issue_date))
                          ? format(new Date(item.issue_date), "MMM yyyy")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`rounded-full px-2 py-1 text-xs ${
                            item.is_published
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {item.is_published ? "Published" : "Draft"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <a
                          href={item.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          View
                        </a>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(item)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(item.id)}
                            className="text-destructive hover:bg-destructive hover:text-white"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : emagazines && emagazines.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {emagazines.map(item => (
                  <Card key={item.id} className="overflow-hidden">
                    {item.cover_image && (
                      <img
                        src={item.cover_image}
                        alt={item.title}
                        className="h-40 w-full object-cover"
                      />
                    )}
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="line-clamp-2 font-semibold">
                          {item.title}
                        </h3>
                        <Badge
                          variant={item.is_published ? "default" : "secondary"}
                        >
                          {item.is_published ? "Published" : "Draft"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {item.volume || "No volume"} · {item.year}
                      </p>
                      <div className="flex gap-2 border-t pt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(item)}
                        >
                          <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground">
                <FileText className="mx-auto mb-3 h-12 w-12 opacity-50" />
                <p>No eMagazine issues yet</p>
                <p className="text-sm">
                  Click "Add Issue" to create your first eMagazine
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
